import React from 'react';
import { BOTTOM_INDENTS, COLORS } from '@constants';
import { useStore } from '@hooks';
import { Button, Typography } from '@ui';
import { observer } from 'mobx-react';
import { useScriptTag } from '../../../../../../../../components/IntegrationKlickly/hooks/useScriptTag';
import { StyledCode, StyledDescription, StyledInvalidParams, StyledSubtitle, StyledText } from './styled';

const AnalyticsScriptModal = observer(() => {
    const { scriptTag, hasRequiredParams } = useScriptTag();
    const store = useStore();
    const { setupInfo, currentAccount, mainEcommerceShopDomain, isShopify } = store.accountsStore;
    const restCustomDomains = setupInfo?.restCustomDomains || [];
    const hasThirdPartyDomain = currentAccount?.settings?.hasThirdPartyDomain || false;

    const addShopDomainToScriptTag = (scriptTagString) => {
        if (!scriptTagString || !mainEcommerceShopDomain) {
            return scriptTagString;
        }

        const urlMatch = scriptTagString.match(/src="([^"]+)"/);
        if (!urlMatch) {
            return scriptTagString;
        }

        const url = urlMatch[1];
        const urlObject = new URL(url);

        if (isShopify) {
            urlObject.searchParams.set('shop', mainEcommerceShopDomain);
        }

        return scriptTagString.replace(/src="[^"]+"/, `src="${urlObject.toString()}"`);
    };

    return (
        <React.Fragment>
            <Typography.Text
                data-e2e='integrations-script-modal_description_text'
                type='descriptor1'
                color={COLORS.$black}
                style={BOTTOM_INDENTS.M}
            >
                In order to identify, build, and target high priority consumers, you&apos;ll need to install your
                site&apos;s analytics script. This script will allow Klickly&apos;s AI models to build consumer
                audiences that most closely match your highest value customers, and supports analytics across your
                campaigns.
            </Typography.Text>
            <StyledSubtitle>For your website:</StyledSubtitle>
            <StyledText
                data-e2e='integrations-script-modal_installation-guide_text'
                type='descriptor1'
                color={COLORS.$black}
                style={BOTTOM_INDENTS.M}
            >
                To install your analytics script:
                <br />
                <ul>
                    <li>Copy the below script</li>
                    <li>
                        Paste it into the <code>&lt;head&gt;</code> section of your website&apos;s code,{' '}
                        <strong>including your thank you page</strong>
                    </li>
                    <li>Click &quot;Confirm connection&quot; to allow Klickly to confirm installation</li>
                </ul>
            </StyledText>
            {hasRequiredParams ? (
                <React.Fragment>
                    <StyledCode data-e2e='integrations-script-modal_code_text' style={BOTTOM_INDENTS.M}>
                        <Button.Copy value={addShopDomainToScriptTag(scriptTag)} />
                        <code>{addShopDomainToScriptTag(scriptTag)}</code>
                    </StyledCode>
                    {hasThirdPartyDomain && restCustomDomains.length > 0 && (
                        <React.Fragment>
                            <StyledSubtitle>For other domains or sub-domains:</StyledSubtitle>
                            <StyledText type='descriptor1' color={COLORS.$black} style={BOTTOM_INDENTS.M}>
                                To install the analytics script on another domain or sub-domain:
                                <br />
                                <ul>
                                    <li>Copy the below script</li>
                                    <li>
                                        Paste it into the <code>&lt;head&gt;</code> section of your other domain /
                                        sub-domain pages code
                                    </li>
                                    <li>
                                        Click &quot;Confirm connection&quot; to allow Klickly to confirm installation
                                    </li>
                                </ul>
                            </StyledText>

                            {restCustomDomains.map((item, index) => (
                                <React.Fragment key={item.domainId}>
                                    <Typography.Text
                                        type='descriptor1'
                                        color={COLORS.$gray60Black}
                                        style={BOTTOM_INDENTS.XS}
                                    >
                                        {item.domain}
                                    </Typography.Text>
                                    <StyledCode
                                        data-e2e={`integrations-script-modal_code_${index}_text`}
                                        style={BOTTOM_INDENTS.S}
                                    >
                                        <Button.Copy value={addShopDomainToScriptTag(item.scriptTag)} />
                                        <code>{addShopDomainToScriptTag(item.scriptTag)}</code>
                                    </StyledCode>
                                </React.Fragment>
                            ))}
                        </React.Fragment>
                    )}
                    <StyledDescription data-e2e='integrations-script-modal_warning_text'>
                        <Typography.Text align='left' type='descriptor1'>
                            Once correctly installed, the status of this step will automatically change to Connected.
                        </Typography.Text>
                    </StyledDescription>
                </React.Fragment>
            ) : (
                <StyledInvalidParams style={BOTTOM_INDENTS.M}>
                    <Typography.Text color={COLORS.$tartRed} align='left' type='descriptor1'>
                        The script will be available shortly. Please refresh the page in a few minutes.
                    </Typography.Text>
                </StyledInvalidParams>
            )}
        </React.Fragment>
    );
});

export default AnalyticsScriptModal;
